import { Website } from '@betterfunnel/types';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import {
  CodeBracketIcon,
  MagnifyingGlassIcon,
  PaintBrushIcon,
  PuzzlePieceIcon,
  Square3Stack3DIcon,
} from '@heroicons/react/24/outline';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { useEffect, useMemo, useState } from 'react';
import { WebsiteUpdate } from '../../contexts/WebsiteUpdaterContext';
import Integrations from '../../screens/Integrations';
import { classNames } from '../../utils';
import ManageWebsiteConfig from '../DynamicPages/ManageWebsiteConfig';
import ManageWebsiteContent from '../DynamicPages/ManageWebsiteContent';
import ManageWebsiteSEO from '../DynamicPages/ManageWebsiteSEO';
import ManageWebsiteStyle from '../DynamicPages/ManageWebsiteStyle';

export type VariantSidebarCollapseMode = 'open' | 'open-on-hover';

export interface SidebarItem {
  name: string;
  id: string;
  icon: any;
  current: boolean;
  count?: number;
}

export interface SidebarTeamItem {
  id: string;
  name: string;
  current: boolean;
  initial: string;
  href: string;
  onOpen: () => any;
  onClose: () => any;
  onCloseEditor?: () => any;
}

interface Props {
  collapsed?: boolean;
  websiteId: string;
  website?: Website;
  onOpen: () => any;
  onClose: () => any;
  onCloseEditor?: () => any;
  onUpdateWebsiteEvent: (_: WebsiteUpdate) => void;
}

export default function EditorSidebar({
  collapsed,
  websiteId,
  website,
  onCloseEditor,
  onOpen,
  onClose,
  onUpdateWebsiteEvent,
}: Props) {
  const [currentTab, setCurrentTab] = useState(null);

  const navigation: any[] = useMemo(() => {
    const items: Omit<SidebarItem, 'current'>[] = [
      {
        name: 'Content',
        id: 'content',
        icon: Square3Stack3DIcon,
      },
      // { name: 'Conversion Goals', id: 'settings', icon: FunnelIcon },
      { name: 'Style', id: 'style', icon: PaintBrushIcon },
      { name: 'Search & Social', id: 'seo', icon: MagnifyingGlassIcon },
      { name: 'Scripts & Privacy', id: 'scripts', icon: CodeBracketIcon },
      // {
      //   name: 'Popups',
      //   id: 'settings',
      //   icon: ChatBubbleBottomCenterIcon,
      // },
      { name: 'Integrations', id: 'integrations', icon: PuzzlePieceIcon },
    ];

    return items;
  }, []);

  useEffect(() => {
    if (collapsed) {
      setCurrentTab(null);
    }
  }, [collapsed]);

  return (
    <>
      <div
        className={classNames(
          'relative flex h-full grow flex-col gap-y-0 overflow-y-auto bg-white transition-all duration-300',
          collapsed ? 'w-8' : 'w-[360px]',
        )}
      >
        <div className="sticky top-0 z-10 flex flex-col items-start gap-y-6 bg-white px-0.5">
          <div className="mt-2 flex flex-row content-center items-center gap-x-3">
            <XMarkIcon
              className="h-6 w-6 shrink-0 cursor-pointer bg-white text-black"
              onClick={() => onCloseEditor && onCloseEditor()}
            />
            {/* {!collapsed && (
              <div className="whitespace-nowrap text-lg font-semibold leading-6">
                Variant #1
              </div>
            )} */}
          </div>
          {currentTab ? (
            <div className="flex w-full flex-row items-center gap-3 bg-white py-2 pb-4">
              <ArrowLeftIcon
                className="h-6 w-6 shrink-0 cursor-pointer text-black"
                onClick={() => setCurrentTab(null)}
              />
              <div className="text-lg font-semibold leading-6">
                {navigation.find((item) => item.id === currentTab)?.name ??
                  currentTab}
              </div>
            </div>
          ) : (
            <div></div>
          )}
        </div>
        {currentTab && (
          <div className="relative flex flex-col gap-4 px-0.5 pt-4">
            {currentTab === 'content' && (
              <ManageWebsiteContent
                websiteId={websiteId}
                website={website}
                onUpdateWebsiteEvent={onUpdateWebsiteEvent}
              />
            )}
            {currentTab === 'integrations' && <Integrations />}
            {currentTab === 'style' && (
              <ManageWebsiteStyle
                websiteId={websiteId}
                website={website}
                onUpdateWebsiteEvent={onUpdateWebsiteEvent}
              />
            )}
            {currentTab === 'seo' && (
              <ManageWebsiteSEO
                websiteId={websiteId}
                website={website}
                onUpdateWebsiteEvent={onUpdateWebsiteEvent}
              />
            )}
            {currentTab === 'scripts' && (
              <ManageWebsiteConfig
                websiteId={websiteId}
                website={website}
                onUpdateWebsiteEvent={onUpdateWebsiteEvent}
              />
            )}
          </div>
        )}
        {!currentTab && (
          <nav className="flex flex-1 flex-col px-0.5">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-3">
                  {navigation.map((item) => (
                    <li key={item.id} className="active:outline-none">
                      <button
                        onClick={() => {
                          setCurrentTab(item.id);
                          onClose();
                        }}
                        className={classNames(
                          item.current
                            ? 'bg-indigo-700 text-white'
                            : 'text-black hover:bg-transparent hover:text-indigo-700',
                          'group flex w-full gap-x-3 whitespace-nowrap rounded-md p-2 text-lg font-semibold leading-6',
                        )}
                      >
                        <item.icon
                          className={classNames(
                            item.current
                              ? 'text-white'
                              : 'text-black group-hover:text-indigo-700',
                            'h-6 w-6 shrink-0',
                          )}
                          aria-hidden="true"
                        />
                        <span
                          className={classNames(
                            'transition-opacity',
                            collapsed ? 'opacity-0' : 'opacity-100',
                          )}
                        >
                          {item.name}
                        </span>
                        {item.count ? (
                          <span
                            className="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-indigo-600 px-2.5 py-0.5 text-center text-xs font-medium leading-5 text-white ring-1 ring-inset ring-indigo-500"
                            aria-hidden="true"
                          >
                            {item.count}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              </li>
            </ul>
          </nav>
        )}
        <div className="flex flex-1 flex-col"></div>
        {!currentTab && (
          <div>
            {collapsed ? (
              <ChevronRightIcon
                onClick={onClose}
                className={classNames(
                  'cursor-pointer text-black hover:text-indigo-700',
                  'mb-2 h-6 w-6 shrink-0',
                )}
                aria-hidden="true"
              />
            ) : (
              <ChevronLeftIcon
                onClick={() => {
                  onOpen();
                  setCurrentTab(null);
                }}
                className={classNames(
                  'cursor-pointer text-black hover:text-indigo-700',
                  'mb-2 h-6 w-6 shrink-0',
                )}
                aria-hidden="true"
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
